"use client"

import React from "react"
import { useState, useEffect } from "react"
import dayjs from "dayjs"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonRefresher, IonRefresherContent, useIonViewWillEnter } from "@ionic/react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  TablePagination,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from "@mui/material"
import {
  Search,
  FilterList,
  Download,
  Visibility,
  Close,
  AttachMoney,
  Receipt,
  Person,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Warning,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers"

// Add this import for getting staff data
// import { getStaffList } from "../../../api/users/users" // Adjust the import path as needed
import { getCashierOrders } from "../../../api/orders/orders"
import { getManagersOrders } from "../../../api/reports/reports"
import { getStaffList } from "../../../api/users/user"
import { MenuButton } from "../../../components/Menu"
// import { getManagersOrders } from "../../../sales app/reports/reports"

interface OrderItem {
  product_id: string
  quantity: string
  product_name: string
  price: string
  total_amount: string
  item_status: string
}

interface SalesTransaction {
  order_id: string
  order_code: string
  order_date: string
  mode_of_payment: string
  payable_amount: string
  balance_due: string
  order_by: string
  order_status: string
  member_id: string
  member_name: string
  payment_status: string
  otp_status: string | null
  items: OrderItem[]
  tabBalance: number
  debtBalance: number
  walletDetails: number
  walletDetails_l: number
}

interface SalesStats {
  total_sales: number
  total_orders: number
  average_order_value: number
  top_payment_method: string
  growth_percentage: number
  pending_orders: number
  completed_orders: number
}

// Add staff state and interface
interface Staff {
  staff_id: string
  staff_name: string
  // Add other staff properties as needed
}

// Update FilterState interface to include staff
interface FilterState {
  startDate: Date | null
  endDate: Date | null
  paymentMethod: string
  department: string
  orderStatus: string
  paymentStatus: string
  staffId: string // Add this line
}

const ManagerSalesReportPage: React.FC = () => {
  const [transactions, setTransactions] = useState<SalesTransaction[]>([])
  const [filteredTransactions, setFilteredTransactions] = useState<SalesTransaction[]>([])
  const [salesStats, setSalesStats] = useState<SalesStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [showTransactionDetail, setShowTransactionDetail] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<SalesTransaction | null>(null)
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Add staff state variables after other state declarations
  const [staffList, setStaffList] = useState<Staff[]>([])

  const [filters, setFilters] = useState<FilterState>({
    startDate: dayjs(),
    endDate: dayjs(),
    paymentMethod: "",
    department: "",
    orderStatus: "",
    paymentStatus: "",
    staffId: "", // Add this line
  })



  const paymentMethods = ["All", "cash", "card", "transfer", "mobile_money"]
  const orderStatuses = ["All", "COMPLETED", "PENDING", "CANCELLED"]
  const paymentStatuses = ["All", "0", "1", "2"] // 0=unpaid, 1=paid, 2=partial
  const departments = ["All", "Food & Beverage", "Electronics", "Clothing", "Books"]

  useEffect(() => {
    fetchSalesData()
  }, [filters,rowsPerPage])

  useEffect(() => {
    applyFilters()
  }, [transactions, searchQuery])

  const fetchStaffData = async () => {
    try {
      const response = await getStaffList()
      console.log({ staffResponse: response })
      if (response.data) {
        setStaffList(response.data)
      }
    } catch (error) {
      console.error("Error fetching staff data:", error)
    }
  }

  // Update fetchSalesData to also fetch staff data
  const fetchSalesData = async () => {
    setLoading(true)
    try {
      // Simulate API call
      const response = await getManagersOrders(rowsPerPage)
      // const response = { data: mockTransactions }
      console.log({ response })

      // Fetch staff data
      await fetchStaffData()

      setTransactions(response.data)


      const totalSales = response.data?.reduce((sum, txn) => sum + Number.parseFloat(txn.payable_amount), 0)
      const completedOrders = response.data?.filter((txn) => txn.order_status === "COMPLETED").length
      const pendingOrders = response.data?.filter((txn) => txn.order_status === "PENDING").length

      setSalesStats({
        total_sales: totalSales,
        total_orders: response.data?.length,
        average_order_value: totalSales / response.data?.length,
        top_payment_method: "cash",
        growth_percentage: 12.5,
        pending_orders: pendingOrders,
        completed_orders: completedOrders,
      })

    } catch (error) {
      console.error("Error fetching sales data:", error)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = transactions ? transactions : []

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(
        (transaction) =>
          transaction.order_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction.order_id.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    }

  if (filters.startDate && filters.endDate) {
  const start = new Date(filters.startDate);
  start.setHours(0, 0, 0, 0); // 00:00:00

  const end = new Date(filters.endDate);
  end.setHours(23, 59, 59, 999); // 23:59:59.999

  filtered = filtered?.filter((transaction) => {
    const transactionDate = new Date(transaction.order_date);
    return transactionDate >= start && transactionDate <= end;
  });
}


    // Apply payment method filter
    if (filters.paymentMethod && filters.paymentMethod !== "All") {
      filtered = filtered?.filter((transaction) => transaction.mode_of_payment === filters.paymentMethod)
    }

    // Apply order status filter
    if (filters.orderStatus && filters.orderStatus !== "All") {
      filtered = filtered?.filter((transaction) => transaction.order_status === filters.orderStatus)
    }

    // Apply payment status filter
    if (filters.paymentStatus && filters.paymentStatus !== "All") {
      filtered = filtered?.filter((transaction) => transaction.payment_status === filters.paymentStatus)
    }

    // Apply staff filter - Add this section
    if (filters.staffId && filters.staffId !== "All") {
      filtered = filtered?.filter((transaction) => transaction.order_by === filters.staffId)
    }

    setFilteredTransactions(filtered)
  }

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const clearFilters = () => {
    setFilters({
      startDate: dayjs(),
      endDate: dayjs(),
      paymentMethod: "",
      department: "",
      orderStatus: "",
      paymentStatus: "",
      staffId: "", // Add this line
    })
    setSearchQuery("")
  }

  const viewTransactionDetail = (transaction: SalesTransaction) => {
    setSelectedTransaction(transaction)
    setShowTransactionDetail(true)
  }

  const toggleRowExpansion = (orderId: string) => {
    setExpandedRows((prev) => (prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]))
  }

  const getOrderStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
        return "success"
      case "PENDING":
        return "warning"
      case "CANCELLED":
        return "error"
      default:
        return "default"
    }
  }

  const getPaymentStatusLabel = (status: string) => {
    switch (status) {
      case "0":
        return "Unpaid"
      case "1":
        return "Paid"
      case "2":
        return "Partial"
      default:
        return "Unknown"
    }
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error"
      case "1":
        return "success"
      case "2":
        return "warning"
      default:
        return "default"
    }
  }

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const exportData = () => {
    // Create CSV content
    const headers = [
      "Order Code",
      "Date",
      "Member Name",
      "Payment Method",
      "Amount",
      "Balance Due",
      "Order Status",
      "Payment Status",
      "Items Count",
    ]

    const csvContent = [
      headers.join(","),
      ...filteredTransactions.map((transaction) =>
        [
          transaction.order_code,
          transaction.order_date,
          `"${transaction.member_name}"`,
          transaction.mode_of_payment,
          transaction.payable_amount,
          transaction.balance_due,
          transaction.order_status,
          getPaymentStatusLabel(transaction.payment_status),
          transaction.items.length,
        ].join(","),
      ),
    ].join("\n")

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `sales-report-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }


  useIonViewWillEnter(() => {
    fetchSalesData()
    fetchStaffData() // Fetch staff data when the page is entered
  })
  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>  <MenuButton/>
          <IonTitle>Manager Sales Report</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher
          slot="fixed"
          onIonRefresh={(e) => {
            fetchSalesData().finally(() => e.detail.complete())
          }}
        >
          <IonRefresherContent />
        </IonRefresher>

        <Box sx={{ p: 2 }}>
          {/* Sales Statistics Cards */}
          {salesStats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "primary.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <AttachMoney sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Total Sales
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {formatCurrency(salesStats.total_sales)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "success.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Receipt sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Total Orders
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {salesStats.total_orders}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "warning.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Warning sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Pending Orders
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {salesStats.pending_orders}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "info.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Completed
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {salesStats.completed_orders}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Search and Filter Controls */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by order code, member name, or order ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                size="small"
              />
              <Button variant="outlined" startIcon={<FilterList />} onClick={() => setShowFilters(!showFilters)}>
                Filters
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={exportData}>
                Export
              </Button>
            </Box>

            {/* Filter Panel */}
            {showFilters && (
              <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="Start Date"
                      value={filters.startDate}
                      onChange={(date) => handleFilterChange("startDate", date)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="End Date"
                      value={filters.endDate}
                      onChange={(date) => handleFilterChange("endDate", date)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        value={filters.paymentMethod}
                        label="Payment Method"
                        onChange={(e) => handleFilterChange("paymentMethod", e.target.value)}
                      >
                        {paymentMethods.map((method) => (
                          <MenuItem key={method} value={method === "All" ? "" : method}>
                            {method}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Order Status</InputLabel>
                      <Select
                        value={filters.orderStatus}
                        label="Order Status"
                        onChange={(e) => handleFilterChange("orderStatus", e.target.value)}
                      >
                        {orderStatuses.map((status) => (
                          <MenuItem key={status} value={status === "All" ? "" : status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Status</InputLabel>
                      <Select
                        value={filters.paymentStatus}
                        label="Payment Status"
                        onChange={(e) => handleFilterChange("paymentStatus", e.target.value)}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="0">Unpaid</MenuItem>
                        <MenuItem value="1">Paid</MenuItem>
                        <MenuItem value="2">Partial</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Staff</InputLabel>
                      <Select
                        value={filters.staffId}
                        label="Staff"
                        onChange={(e) => handleFilterChange("staffId", e.target.value)}
                      >
                        <MenuItem value="">All Staff</MenuItem>
                        {staffList?.map((staff) => (
                          <MenuItem key={staff.user_id} value={staff.user_id}>
                            {staff.firstName}  {staff.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <Button variant="outlined" color="secondary" onClick={clearFilters} fullWidth>
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Results Summary */}
          <Alert severity="info" sx={{ mb: 2 }}>
            Showing {filteredTransactions?.length} of {transactions?.length} orders
          </Alert>

          {/* Transactions Table - Made Horizontally Scrollable */}
          <TableContainer
            component={Paper}
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: 8,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: 4,
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
            }}
          >
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>Order Code</TableCell>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>Date</TableCell>
                  <TableCell sx={{ minWidth: 200, whiteSpace: "nowrap" }}>Member</TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Payment Method</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Amount
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Balance Due
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Order Status</TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Payment Status</TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>Items</TableCell>
                  <TableCell align="center" sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredTransactions?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((transaction) => {
                      const isExpanded = expandedRows.includes(transaction.order_id)
                      return (
                        <React.Fragment key={transaction.order_id}>
                          <TableRow
                            sx={{
                              "&:nth-of-type(even)": { bgcolor: "grey.50" },
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                                {transaction.order_code}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2">{formatDate(transaction.order_date)}</Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Person sx={{ mr: 1, fontSize: 16, color: "text.secondary" }} />
                                <Typography variant="body2">{transaction.member_name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Chip label={transaction.mode_of_payment} size="small" color="secondary" />
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                {formatCurrency(transaction.payable_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "bold",
                                  color: Number.parseFloat(transaction.balance_due) > 0 ? "error.main" : "success.main",
                                }}
                              >
                                {formatCurrency(transaction.balance_due)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Chip
                                label={transaction.order_status}
                                size="small"
                                color={getOrderStatusColor(transaction.order_status) as any}
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Chip
                                label={getPaymentStatusLabel(transaction.payment_status)}
                                size="small"
                                color={getPaymentStatusColor(transaction.payment_status) as any}
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Button
                                size="small"
                                startIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                onClick={() => toggleRowExpansion(transaction.order_id)}
                              >
                                {transaction.items.length} items
                              </Button>
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => viewTransactionDetail(transaction)}
                                  color="primary"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>

                          {/* Expandable Items Row */}
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1 }}>
                                  <Typography variant="h6" gutterBottom component="div">
                                    Order Items
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell align="right">Quantity</TableCell>
                                        <TableCell align="right">Unit Price</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell>Status</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {transaction.items.map((item, index) => (
                                        <TableRow key={`${transaction.order_id}-${index}`}>
                                          <TableCell>{item.product_name}</TableCell>
                                          <TableCell align="right">{item.quantity}</TableCell>
                                          <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                                          <TableCell align="right">{formatCurrency(item.total_amount)}</TableCell>
                                          <TableCell>
                                            <Chip
                                              label={item.item_status}
                                              size="small"
                                              color={getOrderStatusColor(item.item_status) as any}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      )
                    })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredTransactions?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value, 10))
                setPage(0)
              }}
            />
          </TableContainer>
        </Box>

        {/* Transaction Detail Modal */}
        <Dialog open={showTransactionDetail} onClose={() => setShowTransactionDetail(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Order Details - {selectedTransaction?.order_code}
            <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={() => setShowTransactionDetail(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTransaction && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Order Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {selectedTransaction.order_code}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order ID
                    </Typography>
                    <Typography variant="body1">{selectedTransaction.order_id}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1">{formatDate(selectedTransaction.order_date)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order Status
                    </Typography>
                    <Chip
                      label={selectedTransaction.order_status}
                      color={getOrderStatusColor(selectedTransaction.order_status) as any}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Staff
                    </Typography>
                    <Typography variant="body1">{selectedTransaction.order_by}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Member & Payment
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Member Name
                    </Typography>
                    <Typography variant="body1">{selectedTransaction.member_name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Member ID
                    </Typography>
                    <Typography variant="body1">{selectedTransaction.member_id}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Chip label={selectedTransaction.mode_of_payment} color="secondary" sx={{ mt: 0.5 }} />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Chip
                      label={getPaymentStatusLabel(selectedTransaction.payment_status)}
                      color={getPaymentStatusColor(selectedTransaction.payment_status) as any}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main" }}>
                      {formatCurrency(selectedTransaction.payable_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Balance Due
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: Number.parseFloat(selectedTransaction.balance_due) > 0 ? "error.main" : "success.main",
                      }}
                    >
                      {formatCurrency(selectedTransaction.balance_due)}
                    </Typography>
                  </Box>

                  {/* Add the new fields here */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Tab Balance
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {formatCurrency(selectedTransaction.tabBalance)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Debt Balance
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "bold",
                        color: selectedTransaction.debtBalance > 0 ? "error.main" : "success.main",
                      }}
                    >
                      {formatCurrency(selectedTransaction.debtBalance)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Wallet Details
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {formatCurrency(selectedTransaction.walletDetails)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Wallet Details (L)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {formatCurrency(selectedTransaction.walletDetails_l)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Order Items ({selectedTransaction.items.length})
                  </Typography>
                  <List>
                    {selectedTransaction.items.map((item, index) => (
                      <ListItem key={index} divider>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography variant="subtitle1">{item.product_name}</Typography>
                              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                {formatCurrency(item.total_amount)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                              <Typography variant="body2">
                                Qty: {item.quantity} × {formatCurrency(item.price)}
                              </Typography>
                              <Chip
                                label={item.item_status}
                                size="small"
                                color={getOrderStatusColor(item.item_status) as any}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowTransactionDetail(false)}>Close</Button>
            <Button variant="contained" startIcon={<Download />}>
              Download Receipt
            </Button>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  )
}

export default ManagerSalesReportPage
